import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useSession } from '@/store/session'
import { useTheme } from '@/hooks/useTheme'

/**
 * Página de login con GoTrue MapeoData (DEC-003).
 * Identidad visual "Cabina de inteligencia" (Fase 3) — la lógica de auth,
 * el manejo de error, el store de sesión y la redirección se conservan.
 */
export default function Login() {
  const navigate = useNavigate()
  const setSession = useSession((s) => s.setSession)
  const { theme, toggleTheme } = useTheme()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (authError || !data.session) {
        setError(authError?.message || 'Credenciales inválidas')
        return
      }
      setSession({
        user: {
          id: data.user.id,
          email: data.user.email!,
          created_at: data.user.created_at,
        },
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
        expires_at: data.session.expires_at!,
      })
      navigate('/app')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="cabina-root">
      <div className="auth-screen">
        <button
          type="button"
          className="icon-btn auth-theme"
          onClick={toggleTheme}
          title="Cambiar tema"
          aria-label="Cambiar tema"
        >
          {theme === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
            </svg>
          )}
        </button>

        <div className="auth-card">
          <Link to="/" className="auth-brand">
            <span className="mark">M</span>MapeoData
          </Link>
          <h1>Iniciar sesión</h1>
          <p className="auth-sub">Accede a tu cabina de inteligencia de leads.</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="cab-field" style={{ margin: 0 }}>
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="cab-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </div>
            <div className="cab-field" style={{ margin: 0 }}>
              <label htmlFor="login-pass">Contraseña</label>
              <input
                id="login-pass"
                className="cab-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div className="auth-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
                </svg>
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn btn-accent">
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="auth-foot">
            ¿No tienes cuenta? <Link to="/registro">Crea una gratis</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
